/**
 * The device's timezone, on every request, as `X-Timezone`.
 *
 * The API stores calendar dates as sent (the device's own `YYYY-MM-DD`) and
 * moments in UTC, but anything it works out against "today" — an
 * `after:today` rule, a day it stamps itself — needs the person's day rather
 * than the server's. Only the device knows which zone that is, so every
 * request says it: `apiClient`, the auth client and the token refresh.
 *
 * Read per request rather than once, so a device that changes zone mid-session
 * (travel) is never a request behind.
 */

export const TIMEZONE_HEADER = 'X-Timezone';

/** The device's IANA zone, e.g. `America/Vancouver`; null when the runtime cannot say. */
export function getDeviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** The header as a plain object, for `fetch` callers; empty when the zone is unknown. */
export function timeZoneHeaders(): Record<string, string> {
  const zone = getDeviceTimeZone();
  return zone ? { [TIMEZONE_HEADER]: zone } : {};
}

/**
 * An axios request interceptor that stamps the header.
 *
 * `headers` may be an `AxiosHeaders` or the plain object the integration
 * layer's metadata interceptor rebuilds it as, so it sets the header in
 * whichever shape it finds.
 */
export function stampTimeZone<T extends { headers?: unknown }>(config: T): T {
  const zone = getDeviceTimeZone();
  const headers = config.headers as
    | ({ set?: (name: string, value: string) => void } & Record<
        string,
        unknown
      >)
    | undefined;
  if (!zone || !headers) return config;

  if (typeof headers.set === 'function') {
    headers.set(TIMEZONE_HEADER, zone);
  } else {
    headers[TIMEZONE_HEADER] = zone;
  }
  return config;
}
